import { Col, Row } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { get } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineHealthAndSafety,
  MdOutlineSubtitles,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  DATE_FORMAT,
  RELATIONSHIP,
  SOCIAL_REGIMES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Children = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.INFO}
              />
            </Col>
            {get(initialValues, "children", []).map((_, index) => {
              const firstnamePath = `children.${index}.identity.user.firstname`;
              const firstnameField = bindField(firstnamePath, {
                label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
                required: isFieldRequired(firstnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const lastnamePath = `children.${index}.identity.user.lastname`;
              const lastnameField = bindField(lastnamePath, {
                label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
                required: isFieldRequired(lastnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const birthDatePath = `children.${index}.identity.user.birthDate`;
              const birthDateField = bindField(birthDatePath, {
                label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
                required: isFieldRequired(birthDatePath),
                disabled: true,
              });

              const socialRegimePath = `children.${index}.identity.socialRegime`;
              const socialRegimeField = bindField(socialRegimePath, {
                label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
                required: isFieldRequired(socialRegimePath),
                onChange: () => setPercent(initialValues),
              });

              const socialSecurityNumberPath = `children.${index}.identity.socialSecurityNumber`;
              const socialSecurityNumberField = bindField(
                socialSecurityNumberPath,
                {
                  label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
                  required: isFieldRequired(socialSecurityNumberPath),
                  onBlur: () => setPercent(initialValues),
                },
              );

              const showSsn =
                moment()
                  .startOf("year")
                  .diff(
                    moment(
                      get(
                        initialValues,
                        `children.${index}.identity.user.birthDate`,
                      ),
                    ).startOf("year"),
                    "years",
                  ) >= 16;

              return (
                <Col lg={24} xs={24} key={`child-form-${index}`}>
                  <Form
                    type="highlight"
                    title={
                      SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.CHILD
                        .TITLE +
                      " " +
                      (index + 1)
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={firstnameField.path}
                          label={firstnameField.label}
                          required={firstnameField.required}
                          disabled={firstnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              placeholder={
                                SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER
                              }
                              label={firstnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              type="custom"
                              normalizeText={true}
                              defaultValue={firstnameField.defaultValue}
                              {...field}
                              onChange={firstnameField.onChange}
                              onBlur={firstnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={lastnameField.path}
                          label={lastnameField.label}
                          required={lastnameField.required}
                          disabled={lastnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={lastnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER
                              }
                              type="custom"
                              normalizeText={true}
                              defaultValue={lastnameField.defaultValue}
                              {...field}
                              onChange={lastnameField.onChange}
                              onBlur={lastnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={birthDateField.path}
                          label={birthDateField.label}
                          required={birthDateField.required}
                          disabled={birthDateField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={birthDateField.label}
                              icon={<MdOutlineCake size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER
                              }
                              type="date"
                              variant="simple"
                              defaultValue={dayjs(
                                get(initialValues, birthDatePath),
                              )}
                              format={DATE_FORMAT}
                              {...field}
                              onChange={birthDateField.onChange}
                              onBlur={birthDateField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={socialRegimeField.path}
                          label={socialRegimeField.label}
                          required={socialRegimeField.required}
                          disabled={socialRegimeField.disabled}
                        >
                          {(field) => (
                            <Select
                              label={socialRegimeField.label}
                              icon={<MdOutlineHealthAndSafety size={16} />}
                              optional={SUBSCRIPTION.FORM.OPTIONAL}
                              placeholder={
                                SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER
                              }
                              type="simple"
                              options={Object.entries(SOCIAL_REGIMES).map(
                                ([key, value]) => ({
                                  value,
                                  label:
                                    SUBSCRIPTION.FORM.MANDATORY_SCHEME.OPTIONS[
                                      key
                                    ],
                                }),
                              )}
                              defaultValue={socialRegimeField.defaultValue}
                              {...field}
                              onChange={socialRegimeField.onChange}
                              onBlur={socialRegimeField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col
                        lg={24}
                        xs={24}
                        className={classNames({ hidden: !showSsn })}
                      >
                        <FormField
                          path={socialSecurityNumberField.path}
                          label={socialSecurityNumberField.label}
                          required={socialSecurityNumberField.required}
                          disabled={socialSecurityNumberField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={socialSecurityNumberField.label}
                              icon={<MdOutlineSubtitles size={16} />}
                              type="custom"
                              trim={true}
                              placeholder={
                                SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER
                                  .PLACEHOLDER
                              }
                              defaultValue={
                                socialSecurityNumberField.defaultValue
                              }
                              {...field}
                              onChange={socialSecurityNumberField.onChange}
                              onBlur={socialSecurityNumberField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              );
            })}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
